import identity from 'lodash/identity';
import type { AutoCrudHttp } from '@wix/auto-crud';
import type { FiltersMap, OptionalFiltersMap } from '../model';
import type { CollectionState } from '../state';
import type { CollectionConfigWithConditionals } from './collectionConfigPartials';
import type { CursorQuery, OffsetQuery } from '.';
import { useWixPatternsContainer } from '../providers';
import { useCreateCollection } from './useCollection';
import { useState } from 'react';
import { WixPatternsContainerParams } from '../state';

export type TransformPlatformizedQuery = (
  platformizedQuery: PlatformizedQuery,
  wixPatternsQuery: CursorQuery,
) => PlatformizedQuery | Promise<PlatformizedQuery>;

export type AmbassadorCollectionConfig<
  T,
  F extends FiltersMap = OptionalFiltersMap,
> = Omit<
  CollectionConfigWithConditionals<T, F>,
  'fetchData' | 'paginationMode' | 'queryName'
> &
  AmbassadorCollectionConfigI<T>;

export interface AmbassadorCollectionConfigI<T> {
  /**
   * CRUD subpackage of a metro Ambassador package.
   */
  ambassador: Required<Pick<AutoCrudHttp<T>, 'queryEntities' | 'fqdn'>>;
  /**
   *  A function to transform the cairo query to a PlatformizedQuery object (which will be used to fetch data from the server):
   * - `platformizedQuery`: PlatformizedQuery
   * - `wixPatternsQuery`: CursorQuery
   * The function should return a PlatformizedQuery instance or a promise of PlatformizedQuery
   */
  transformPlatformizedQuery?: TransformPlatformizedQuery;
  /**
   * The query name used for cache purposes. This should be a unique value per API endpoint.
   * @default ambassador.fqdn
   */
  queryName?: string;
}

type PlatformizedFilter = {
  [filter: string]: any;
};

type PlatformizedSorting = {
  fieldName: string;
  order: 'ASC' | 'DESC';
};

type PlatformizedCursorPaging = {
  limit: number;
  cursor?: string | null;
};

// TODO: should be exposed by Ambassador
type PlatformizedQuery = {
  filter?: PlatformizedFilter;
  sort?: PlatformizedSorting[];
  cursorPaging?: PlatformizedCursorPaging;
};

export type AmbassadorCollectionFactory = <
  T,
  F extends FiltersMap = OptionalFiltersMap,
>(
  config: AmbassadorCollectionConfig<T, F>,
) => CollectionState<T, F>;

async function fetchData<T, F extends FiltersMap = OptionalFiltersMap>({
  config,
  wixPatternsQuery,
  httpClient,
  errorHandler,
}: {
  config: AmbassadorCollectionConfig<T, F>;
  wixPatternsQuery: CursorQuery<Partial<F>> | OffsetQuery<Partial<F>>;
  httpClient: WixPatternsContainerParams['httpClient'];
  errorHandler: WixPatternsContainerParams['errorHandler'];
}) {
  const { ambassador } = config;
  const transformPlatformizedQuery =
    config.transformPlatformizedQuery || identity;
  const platformizedQuery = defaultPlatformizedQuery(wixPatternsQuery);
  const query = await transformPlatformizedQuery(
    platformizedQuery,
    wixPatternsQuery,
  );
  return errorHandler?.withErrorHandler(
    () => httpClient.request(ambassador.queryEntities({ query })),
    {},
  );
}

function getOffsetConfig<T, F extends FiltersMap = OptionalFiltersMap>({
  config,
  httpClient,
  errorHandler,
}: {
  config: AmbassadorCollectionConfig<T, F>;
  httpClient: WixPatternsContainerParams['httpClient'];
  errorHandler: WixPatternsContainerParams['errorHandler'];
}): CollectionConfigWithConditionals<T, F> {
  return {
    queryName: config.ambassador.fqdn,
    ...config,
    paginationMode: 'offset',
    fetchData: async (query: OffsetQuery<Partial<F>>) => {
      const response = await fetchData({
        config,
        wixPatternsQuery: query,
        httpClient,
        errorHandler,
      });
      return {
        items: response?.data.entities ?? [],
        total: response?.data.metadata.total ?? 0,
      };
    },
  };
}

function getCursorConfig<T, F extends FiltersMap = OptionalFiltersMap>({
  config,
  httpClient,
  errorHandler,
}: {
  config: AmbassadorCollectionConfig<T, F>;
  httpClient: WixPatternsContainerParams['httpClient'];
  errorHandler: WixPatternsContainerParams['errorHandler'];
}): CollectionConfigWithConditionals<T, F> {
  const { ambassador } = config;

  return {
    queryName: ambassador.fqdn,
    ...config,
    paginationMode: 'cursor',
    fetchData: async (query: CursorQuery<Partial<F>>) => {
      const response = await fetchData({
        config,
        wixPatternsQuery: query,
        httpClient,
        errorHandler,
      });
      return {
        items: response?.data.entities ?? [],
        cursor: response?.data.metadata.cursors.next,
        total: response?.data.metadata.total ?? 0,
      };
    },
  };
}

export function useCreateAmbassadorCollection(): AmbassadorCollectionFactory {
  const createCollection = useCreateCollection();
  const { httpClient, errorHandler } = useWixPatternsContainer();

  return <T, F extends FiltersMap = OptionalFiltersMap>(
    config: AmbassadorCollectionConfig<T, F>,
  ) => {
    const collectionConfig: CollectionConfigWithConditionals<T, F> =
      config.ambassador.queryEntities.paginationMode === 'offset'
        ? getOffsetConfig({ config, httpClient, errorHandler })
        : getCursorConfig({ config, httpClient, errorHandler });

    return createCollection(collectionConfig);
  };
}

export function useAmbassadorCollection<
  T,
  F extends FiltersMap = OptionalFiltersMap,
>(config: AmbassadorCollectionConfig<T, F>): CollectionState<T, F> {
  const createAmbassadorCollection = useCreateAmbassadorCollection();

  const [collection] = useState(() => createAmbassadorCollection<T, F>(config));

  return collection;
}

function defaultPlatformizedQuery(
  wixPatternsQuery: CursorQuery,
): PlatformizedQuery {
  return {
    filter: {},
    sort: wixPatternsQuery.sort?.map((sort) => ({
      fieldName: sort.fieldName,
      order: sort.order.toUpperCase() as 'ASC' | 'DESC',
    })),
    cursorPaging: {
      cursor: wixPatternsQuery.cursor,
      limit: wixPatternsQuery.limit,
    },
  };
}
